import { Fragment } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { SiteSettingsDto } from '@portfolio/shared';
import { cn } from '@/lib/utils';
import { SmartLink } from '../SmartLink';

const ease = [0.16, 1, 0.3, 1] as const;

export function Hero({ s }: { s: SiteSettingsDto }) {
  const reduce = useReducedMotion();
  // Without the portrait (turned off in the admin, or none uploaded) the headline is centred
  // from md up; on mobile it stays start-aligned.
  const portrait = s.heroShowPortrait ? s.heroPortrait : null;
  const lightLines = s.heroLightText
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const anim = (delay: number) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 28 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.9, delay, ease },
        };

  // Title reveal (Framer-style): each word rises and un-blurs in turn.
  // "I don't design posts" plays first, then after a short beat "I design impact", then the buttons.
  const WORD_STAGGER = 0.09;
  const BEAT = 0.45;
  let clock = 0.15;
  const words = (text: string) =>
    text
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => ({ w, delay: (clock += WORD_STAGGER) - WORD_STAGGER }));
  const lightWords = lightLines.map(words);
  clock += BEAT;
  const boldWords = words(s.heroBoldText);
  const highlightWords = words(s.heroHighlightText);
  const buttonsDelay = clock + BEAT;

  const renderWords = (items: { w: string; delay: number }[], dot = false) => (
    <>
      {items.map(({ w, delay }, i) => (
        <Fragment key={i}>
          {i > 0 && ' '}
          <motion.span
            className="inline-block whitespace-nowrap"
            {...(reduce
              ? {}
              : {
                  initial: { opacity: 0, y: '0.35em', filter: 'blur(12px)' },
                  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
                  transition: { duration: 0.8, delay, ease },
                })}
          >
            {w}
            {dot && i === items.length - 1 && (
              <span
                aria-hidden
                className="ms-[0.06em] inline-block size-[0.095em] rounded-full bg-ink align-baseline"
              />
            )}
          </motion.span>
        </Fragment>
      ))}
    </>
  );

  return (
    <section id="top" aria-labelledby="hero-title" className="relative isolate overflow-hidden">
      {portrait && (
        <>
          {/* Warm gradient panel at the end side (left on Arabic pages), as in the reference */}
          <div
            aria-hidden
            className="absolute inset-y-0 end-0 -z-10 hidden w-[31%] bg-[linear-gradient(180deg,#FFFFFF_0%,#FDE3C0_38%,#FBB866_72%,#FA9A21_100%)] dark:bg-[linear-gradient(180deg,var(--color-canvas)_0%,#3A2508_38%,#9A5A12_72%,#FA9A21_100%)] md:block"
          />
          {/* Soft white bloom that fades the panel into the page */}
          <div
            aria-hidden
            className="absolute end-[14%] top-[18%] -z-10 hidden h-[95%] w-[46%] rounded-full bg-canvas blur-[60px] md:block"
          />
        </>
      )}

      <div
        className={cn(
          'container-site relative grid pt-[88px] md:min-h-[min(900px,100svh)] md:pt-[120px]',
          portrait && 'md:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)]',
        )}
      >
        <div
          className={cn(
            'relative z-10 flex flex-col pb-10 md:pb-[150px]',
            !portrait && 'md:items-center md:justify-center md:text-center',
          )}
        >
          <h1
            id="hero-title"
            className="text-[clamp(3.6rem,1.4rem+8.6vw,7.75rem)] uppercase [&>span]:leading-[0.895]"
          >
            {lightWords.map((items, i) => (
              <span key={i} className="display-thin block tracking-[-0.025em]">
                {renderWords(items)}
              </span>
            ))}
            <span className="display-black block tracking-normal">{renderWords(boldWords)}</span>
            <span className="display-black block tracking-normal text-primary">
              {renderWords(highlightWords, true)}
            </span>
          </h1>
          <motion.div
            className={cn(
              'mt-10 flex flex-wrap gap-[11px] md:mt-[42px]',
              !portrait && 'md:justify-center',
            )}
            {...anim(buttonsDelay)}
          >
            <SmartLink
              href={s.heroPrimaryUrl}
              className="inline-flex h-12 items-center rounded-lg bg-ink px-6 text-ui font-semibold text-canvas transition-colors hover:bg-primary"
            >
              {s.heroPrimaryLabel}
            </SmartLink>
            {s.heroSecondaryLabel && s.heroSecondaryUrl && (
              <SmartLink
                href={s.heroSecondaryUrl}
                className="inline-flex h-12 items-center rounded-lg border border-line bg-canvas/70 px-6 text-ui text-graphite transition-colors hover:border-ink hover:text-ink"
              >
                {s.heroSecondaryLabel}
              </SmartLink>
            )}
          </motion.div>
        </div>

        {/* Portrait — mirrored on Arabic pages so it faces the headline, which moves to the right */}
        {portrait && (
          <div className="relative -mx-5 h-[min(118vw,560px)] overflow-hidden bg-[linear-gradient(180deg,#FFF6EA_0%,#FCCB87_60%,#FA9A21_100%)] max-md:dark:bg-[linear-gradient(180deg,#24180A_0%,#8A5212_60%,#FA9A21_100%)] md:static md:mx-0 md:h-auto md:overflow-visible md:bg-none">
            <motion.img
              src={portrait.url}
              alt={portrait.alt ?? s.ownerName}
              width={portrait.width ?? 1051}
              height={portrait.height ?? 1198}
              fetchPriority="high"
              decoding="async"
              className="absolute bottom-0 left-1/2 h-[96%] w-auto max-w-none -translate-x-1/2 object-contain object-bottom md:start-auto md:-end-[4%] md:h-[92%] md:translate-x-0 lg:-end-[2%] rtl:-scale-x-100"
              {...(reduce
                ? {}
                : {
                    initial: { opacity: 0, scale: 1.03 },
                    animate: { opacity: 1, scale: 1 },
                    transition: { duration: 1.2, ease },
                  })}
            />
          </div>
        )}
      </div>
      <div className="container-site">
        <div className="h-px bg-line" />
      </div>
    </section>
  );
}
